import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "../components/sidebar/Sidebar.js";
import { SettingsModal } from "../components/shell/SettingsModal.js";
import { ConfirmProvider } from "../components/shell/ConfirmProvider.js";
import { ToastProvider } from "../components/shell/ToastProvider.js";
import { SubjectDialogProvider } from "../components/subjects/SubjectDialogProvider.js";
import { MapEditingProvider } from "../components/maps/MapEditingContext.js";
import { hasDirtyWork, flushAll } from "../api/dirtyTracker.js";
import { isSaveInFlight } from "../api/saveStatus.js";
import { GridSettingsProvider } from "../preferences/GridSettingsProvider.js";
import { FirstSubjectGate } from "../components/subjects/FirstSubjectGate.js";
import { getSession, getSubjectList } from "../api/queries.js";
import { useQuery } from "../api/useQuery.js";
import { usePreferences } from "../preferences/usePreferences.js";
import { useServerLanguage } from "../preferences/useServerLanguage.js";
import { useTranslationOverrides } from "../preferences/useTranslationOverrides.js";
import { recordLastViewedPage } from "../lastViewedPage.js";
import { TopBar } from "../components/shell/TopBar.js";
import type { SettingsSection } from "../components/shell/SettingsModal.js";
import { SettingsLauncherProvider } from "../components/shell/SettingsLauncherContext.js";
import { useRobotsMeta } from "../seo/useRobotsMeta.js";

/**
 * One key per "workspace": top-level sections (home, tasks, calendar,
 * subjects list) and each subject/chapter. Changing key remounts the routed
 * content so no per-screen state (drafts, selected month, rename mode) bleeds
 * between workspaces; navigation WITHIN a workspace keeps the same key. The
 * swap itself is deliberately instant — content changes in place under the
 * persistent shell, with no animation to read as a page load.
 */
function workspaceKey(pathname: string): string {
  const seg = pathname.split("/").filter(Boolean);
  if (seg[0] === "subjects" && seg[1]) {
    if (seg[2] === "chapters" && seg[3]) return `subjects/${seg[1]}/chapters/${seg[3]}`;
    return `subjects/${seg[1]}`;
  }
  return seg[0] ?? "home";
}

export function AppShell() {
  useRobotsMeta("noindex, nofollow");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSection>("account");
  const openSettings = (section: SettingsSection = "account") => { setSettingsSection(section); setSettingsOpen(true); };
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const sectionKey = workspaceKey(location.pathname);
  const subjectsQuery = useQuery(getSubjectList, undefined);
  const sessionQuery = useQuery(getSession, undefined);
  // Whose empty account this is. Under impersonation that's the impersonated
  // user, so their gate state is theirs — not the superadmin's.
  const { preferences, loaded: preferencesLoaded, save: savePreferences } = usePreferences();
  // Mounted inside the authenticated shell: the saved language can only be
  // applied once a session exists (see useServerLanguage).
  useServerLanguage();
  useTranslationOverrides();
  // Mirrors the stored choice so choosing "Explore by myself" hides the gate
  // immediately rather than waiting on the round trip.
  const [exploreChosen, setExploreChosen] = useState(false);

  // Held back until the session is known: acting on `subjects.length === 0`
  // before then would flash the gate at a user who has simply not loaded yet.
  const firstSubjectRequired =
    subjectsQuery.data?.length === 0 &&
    preferencesLoaded &&
    !exploreChosen &&
    !preferences.exploredWithoutSubject;

  // The main pane is the scroll container and persists across routes — reset
  // it so a scrolled page doesn't hand its offset to the next one.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    recordLastViewedPage(location.pathname);
  }, [location.pathname]);

  // WP-08: the one and only `beforeunload` listener in the app. Any unsaved
  // draft (WritingPanel, a test-attempt free-text answer) or an in-flight
  // save blocks the close with the browser's generic "leave site?" prompt —
  // this deliberately does NOT cover SPA route changes (those don't fire
  // beforeunload, and no router-level navigation blocking was asked for).
  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (hasDirtyWork() || isSaveInFlight()) {
        event.preventDefault();
      }
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Best-effort flush when the tab is backgrounded (covers "switched tabs
  // and forgot" as well as most tab-close paths, since visibilitychange
  // fires before beforeunload). This is NOT a guarantee: true persistence on
  // unload would need `navigator.sendBeacon`, which can't attach the
  // Authorization bearer header our API requires, so a genuinely instant
  // close can still race ahead of this. That gap is accepted — the
  // beforeunload prompt above is the real backstop.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        void flushAll();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return (
    <ToastProvider>
      <ConfirmProvider>
        <SubjectDialogProvider>
          <GridSettingsProvider>
            <SettingsLauncherProvider open={openSettings}>
            <MapEditingProvider>
              <div className="mmm-app-frame" inert={firstSubjectRequired ? true : undefined}>
                <TopBar onOpenSettings={openSettings} />
                <div className="mmm-shell">
                  <Sidebar />
                  <main className="mmm-main" ref={mainRef}>
                    <div key={sectionKey}>
                      <Outlet />
                    </div>
                  </main>
                </div>
              </div>
              {firstSubjectRequired && (
                <FirstSubjectGate
                  // The impersonated user's name when impersonating — the gate
                  // is about THEIR empty account, so greeting the superadmin
                  // here would be plainly wrong.
                  userName={sessionQuery.data?.actingAs.displayName}
                  onExplore={() => {
                    setExploreChosen(true);
                    void savePreferences({ exploredWithoutSubject: true });
                  }}
                />
              )}
              {settingsOpen && <SettingsModal initialSection={settingsSection} onClose={() => setSettingsOpen(false)} />}
            </MapEditingProvider>
            </SettingsLauncherProvider>
          </GridSettingsProvider>
        </SubjectDialogProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
